import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { getFunnelAnalytics } from '@/lib/analytics/funnel-tracking';
import { supabase } from '@/integrations/supabase/client';
import { RefreshCw } from 'lucide-react';
import { 
  processFunnelData, 
  type ProcessedFunnelData,
  type RawFunnelData,
  type StarRatingData,
  type TagScanData,
  type RewardTrackingData
} from '@/lib/analytics/funnel-flow-calculator';
import ImprovedFunnelVisualization from './ImprovedFunnelVisualization';
import ImprovedSankeyVisualization from './ImprovedSankeyVisualization';

interface ReviewProcessFunnelV2Props {
  businessId: string;
  starThreshold?: number;
}

const ReviewProcessFunnelV2: React.FC<ReviewProcessFunnelV2Props> = ({ 
  businessId, 
  starThreshold = 4 
}) => {
  const [processedData, setProcessedData] = useState<ProcessedFunnelData | null>(null);
  const [starRatings, setStarRatings] = useState<StarRatingData[]>([]);
  const [tagScans, setTagScans] = useState<TagScanData[]>([]);
  const [availableForms, setAvailableForms] = useState<{id: string, title: string}[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<string>('all');
  const [formConfig, setFormConfig] = useState<{
    enable_step_2: boolean, 
    reward_enabled: boolean,
    high_rating_email_enabled?: boolean,
    high_rating_email_required?: boolean,
    low_rating_email_enabled?: boolean,
    low_rating_email_required?: boolean
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(7);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const fetchAvailableForms = async () => {
    try {
      const { data: forms } = await supabase
        .from('review_forms')
        .select('id, title, enable_step_2, reward_enabled, high_rating_email_enabled, high_rating_email_required, low_rating_email_enabled, low_rating_email_required')
        .eq('business_id', businessId)
        .eq('active_status', true)
        .order('created_at', { ascending: false });

      if (forms) {
        setAvailableForms(forms.map(form => ({ id: form.id, title: form.title })));
        
        // If a specific form is selected, set its configuration
        if (selectedFormId !== 'all') {
          const selectedForm = forms.find(form => form.id === selectedFormId);
          if (selectedForm) {
            setFormConfig({
              enable_step_2: selectedForm.enable_step_2,
              reward_enabled: selectedForm.reward_enabled,
              high_rating_email_enabled: selectedForm.high_rating_email_enabled,
              high_rating_email_required: selectedForm.high_rating_email_required,
              low_rating_email_enabled: selectedForm.low_rating_email_enabled,
              low_rating_email_required: selectedForm.low_rating_email_required
            });
          }
        } else {
          setFormConfig(null); // Reset for "all forms" view
        }
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
    }
  };

  const fetchAndProcessData = async () => {
    try {
      setLoading(true);
      
      // Get raw funnel data
      const rawData = await getFunnelAnalytics(businessId, timeRange);
      
      // Filter by form_id if specific form is selected
      const filteredData = selectedFormId === 'all' 
        ? rawData 
        : Array.isArray(rawData) 
          ? rawData.filter((event: any) => event.form_id === selectedFormId)
          : rawData;
      
      // If viewing "all forms" but only one form has data, get its config for abandonment calculation
      let detectedFormConfig = null;
      if (selectedFormId === 'all' && Array.isArray(filteredData) && filteredData.length > 0) {
        const uniqueFormIds = [...new Set(filteredData.map((event: any) => event.form_id).filter(Boolean))];
        console.log('🔍 Form detection debug:', {
          selectedFormId,
          filteredDataLength: filteredData.length,
          uniqueFormIds,
          sampleEvents: filteredData.slice(0, 3).map(e => ({ form_id: e.form_id, step_name: e.step_name }))
        });
        
        if (uniqueFormIds.length === 1) {
          // Only one form has data, get its configuration
          try {
            const { data: formData } = await supabase
              .from('review_forms')
              .select('enable_step_2, reward_enabled, high_rating_email_enabled, high_rating_email_required, low_rating_email_enabled, low_rating_email_required')
              .eq('id', uniqueFormIds[0])
              .single();
            
            if (formData) {
              detectedFormConfig = {
                enable_step_2: formData.enable_step_2,
                reward_enabled: formData.reward_enabled,
                high_rating_email_enabled: formData.high_rating_email_enabled,
                high_rating_email_required: formData.high_rating_email_required,
                low_rating_email_enabled: formData.low_rating_email_enabled,
                low_rating_email_required: formData.low_rating_email_required
              };
              console.log('🔍 Auto-detected form config for all forms view:', detectedFormConfig);
            }
          } catch (error) {
            console.error('Error fetching detected form config:', error);
          }
        } else if (uniqueFormIds.length > 1) {
          console.log('🔍 Multiple forms have data, cannot auto-detect config:', uniqueFormIds);
        } else {
          console.log('🔍 No form IDs found in data');
        }
      }
      
      // Fetch all tags for this business
      const { data: allTags } = await supabase
        .from('qr_codes')
        .select('id, name')
        .eq('business_id', businessId);

      // Process tag scanning data
      const tagCounts: Record<string, { name: string; count: number }> = {};
      
      if (allTags) {
        allTags.forEach(tag => {
          tagCounts[tag.id] = { name: tag.name, count: 0 };
        });
      }
      
      const pageLoadEvents = Array.isArray(filteredData) ? 
        filteredData.filter((event: any) => event.step_name === 'page_loaded') : [];
      
      pageLoadEvents.forEach((event: unknown) => {
        const tagId = event.tag_id;
        if (tagId && tagCounts[tagId]) {
          tagCounts[tagId].count++;
        }
      });

      const totalTagScans = Object.values(tagCounts).reduce((sum, tag) => sum + tag.count, 0);
      const tagScanData: TagScanData[] = Object.entries(tagCounts).map(([tagId, data]) => ({
        tagId,
        tagName: data.name,
        count: data.count,
        percentage: totalTagScans > 0 ? (data.count / totalTagScans) * 100 : 0
      })).sort((a, b) => b.count - a.count);

      // Process star rating data
      const ratingCounts: Record<number, number> = {};
      const ratingSelectionEvents = Array.isArray(filteredData) ? 
        filteredData.filter((event: any) => event.step_name === 'rating_selected') : [];
      
      ratingSelectionEvents.forEach((event: unknown) => {
        const rating = event.step_data?.rating;
        if (rating >= 1 && rating <= 5) {
          ratingCounts[rating] = (ratingCounts[rating] || 0) + 1;
        }
      });

      const totalRatings = Object.values(ratingCounts).reduce((sum, count) => sum + count, 0);
      const starRatingData: StarRatingData[] = [1, 2, 3, 4, 5].map(rating => ({
        rating,
        count: ratingCounts[rating] || 0,
        percentage: totalRatings > 0 ? ((ratingCounts[rating] || 0) / totalRatings) * 100 : 0
      }));

      setStarRatings(starRatingData);

      // Process reward tracking data
      const rewardSteps = [
        'reward_offered', 'reward_accepted', 'reward_declined', 
        'reward_email_sent', 'pass_download_started', 'pass_downloaded', 
        'pass_installed', 'reward_redeemed'
      ];
      
      const rewardEvents = Array.isArray(filteredData) ? 
        filteredData.filter((event: any) => rewardSteps.includes(event.step_name)) : [];
      
      const rewardStepCounts: Record<string, { count: number; totalValue: number; rewardType: string }> = {};
      rewardEvents.forEach((event: unknown) => {
        const step = event.step_name;
        const rewardType = event.reward_type || 'unknown';
        const rewardValue = parseFloat(event.reward_value) || 0;
        
        if (!rewardStepCounts[step]) {
          rewardStepCounts[step] = { count: 0, totalValue: 0, rewardType };
        }
        rewardStepCounts[step].count++;
        rewardStepCounts[step].totalValue += rewardValue;
      });

      const totalRewardEvents = Object.values(rewardStepCounts).reduce((sum, data) => sum + data.count, 0);
      const rewardTrackingData: RewardTrackingData[] = rewardSteps.map(step => {
        const data = rewardStepCounts[step] || { count: 0, totalValue: 0, rewardType: 'unknown' };
        return {
          step,
          count: data.count,
          rewardType: data.rewardType,
          totalValue: data.totalValue,
          avgValue: data.count > 0 ? data.totalValue / data.count : 0,
          percentage: totalRewardEvents > 0 ? (data.count / totalRewardEvents) * 100 : 0
        };
      });

      // Process funnel steps
      let funnelData: RawFunnelData[] = [];
      
      if (Array.isArray(filteredData) && filteredData.length > 0 && 'conversionRate' in filteredData[0]) {
        funnelData = filteredData;
      } else {
        // Process raw events into funnel data
        const stepCounts: Record<string, Set<string>> = {};
        
        filteredData.forEach((event: any) => {
          const step = event.step_name;
          const sessionId = event.session_id;
          
          if (!stepCounts[step]) {
            stepCounts[step] = new Set();
          }
          stepCounts[step].add(sessionId);
        });

        const stepOrder = [
          'page_loaded', 'rating_selected', 
          'reward_step_shown', 'email_step_shown', // NEW: reward step for high ratings
          'feedback_form_shown', // Low ratings go here
          'email_entered', 'email_skipped', 'form_abandoned', // NEW: detailed email tracking
          'redirected_to_google', 'review_submitted', 
          'custom_questions_answered', 'process_completed'
        ];

        funnelData = stepOrder.map((step, index) => {
          const current = stepCounts[step]?.size || 0;
          const previous = index > 0 ? stepCounts[stepOrder[index - 1]]?.size || 0 : 0;
          
          const conversionRate = previous > 0 ? (current / previous) * 100 : (current > 0 ? 100 : 0);

          return {
            step,
            count: current,
            conversionRate: Math.round(conversionRate * 100) / 100,
            dropOff: previous - current
          };
        }).filter(c => c.count > 0 || ['page_loaded', 'rating_selected', 'reward_step_shown', 'email_step_shown', 'feedback_form_shown'].includes(c.step));
      }

      // Process all data through the unified calculator
      const processed = processFunnelData(
        funnelData,
        starRatingData,
        tagScanData,
        rewardTrackingData,
        starThreshold
      );

      setProcessedData(processed);
      setLastUpdated(new Date());
      
      // If we detected form config for "all forms" view, temporarily set it
      if (detectedFormConfig && selectedFormId === 'all') {
        setFormConfig(detectedFormConfig);
      }
      
    } catch (error) {
      console.error('Error fetching funnel data:', error);
      toast.error("Error", {
        description: "Failed to load funnel data"
      });
    } finally {
      setLoading(false);
    }
  };

  // Separate effect to update form config when selectedFormId changes
  useEffect(() => {
    const updateFormConfig = async () => {
      console.log('🔄 Updating form config for selectedFormId:', selectedFormId);
      
      if (selectedFormId !== 'all') {
        try {
          const { data: selectedForm } = await supabase
            .from('review_forms')
            .select('enable_step_2, reward_enabled')
            .eq('id', selectedFormId)
            .single();
          
          console.log('📋 Fetched form config:', selectedForm);
          
          if (selectedForm) {
            const config = {
              enable_step_2: selectedForm.enable_step_2,
              reward_enabled: selectedForm.reward_enabled,
              high_rating_email_enabled: selectedForm.high_rating_email_enabled,
              high_rating_email_required: selectedForm.high_rating_email_required,
              low_rating_email_enabled: selectedForm.low_rating_email_enabled,
              low_rating_email_required: selectedForm.low_rating_email_required
            };
            setFormConfig(config);
            console.log('✅ Form config set:', config);
          }
        } catch (error) {
          console.error('❌ Error fetching form config:', error);
          setFormConfig(null);
        }
      } else {
        console.log('🔄 Resetting form config (all forms selected)');
        setFormConfig(null); // Reset for "all forms" view
      }
    };

    updateFormConfig();
  }, [selectedFormId]);

  useEffect(() => {
    if (businessId) {
      fetchAvailableForms();
      fetchAndProcessData();
    }
  }, [businessId, timeRange, selectedFormId]);

  const handleRefresh = () => {
    fetchAndProcessData();
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">Review Process Flow (v2)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!processedData) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">Review Process Flow (v2)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">No data available for the selected time range.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
        <CardTitle className="text-sm font-medium">Review Process Analytics</CardTitle>
        <div className="flex items-center space-x-3 ml-4">
          <Select value={selectedFormId} onValueChange={setSelectedFormId}>
            <SelectTrigger className="w-52 h-9 bg-background border">
              <SelectValue placeholder="Select review form" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">📊 All Review Forms</SelectItem>
              {availableForms.map(form => (
                <SelectItem key={form.id} value={form.id}>
                  📝 {form.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={timeRange.toString()} onValueChange={(value) => setTimeRange(parseInt(value))}>
            <SelectTrigger className="w-36 h-9 bg-background border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">📅 Last 7 days</SelectItem>
              <SelectItem value="14">📅 Last 14 days</SelectItem>
              <SelectItem value="30">📅 Last 30 days</SelectItem>
              <SelectItem value="90">📅 Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
            className="h-9 px-4"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {processedData.metrics.totalSessions === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
              <RefreshCw className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-2">No Review Data Available</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              The customer journey flow will appear once customers start submitting reviews through your QR codes and forms.
            </p>
          </div>
        ) : (
          <>
            <ImprovedSankeyVisualization 
              data={processedData}
              isVertical={true}
              starRatings={starRatings}
              tagScans={tagScans}
              starThreshold={starThreshold}
              formConfig={formConfig}
            />
            
            
            <div className="mt-8 pt-4 border-t">
              <div className="flex justify-between items-center text-xs text-muted-foreground">
                <span>Last updated: {lastUpdated.toLocaleString()}</span>
                <span>Data from last {timeRange} days • {processedData.metrics.totalSessions} total sessions</span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ReviewProcessFunnelV2;